import { type CSSProperties, useMemo } from 'react';
import { useAnchorable } from '../../hooks/useAnchorable';
import { section } from './Section.css';
import type { TPostSectionProps } from './Section.types';

const DEFAULT_BORDER_COLOR = '#222222';

export const PostSection = (props: TPostSectionProps) => {
  const { element, children } = props;
  const { attrs } = element;
  const { anchorAttributes } = useAnchorable(attrs);

  const {
    useIndividualBorderRadius,
    borderTopLeftRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    borderTopRightRadius,
    useIndividualBorderWidth,
    borderWidthTop,
    borderWidthLeft,
    borderWidthBottom,
    borderWidthRight,
    borderColor,
    borderStyle,
    color,
  } = attrs;

  const hasBorders = useMemo(
    () => [borderWidthLeft, borderWidthTop, borderWidthRight, borderWidthBottom].some((value) => value !== undefined),
    [borderWidthLeft, borderWidthTop, borderWidthRight, borderWidthBottom],
  );

  const borderStyles: CSSProperties = useMemo(() => {
    if (!hasBorders) {
      return {};
    }

    return {
      borderColor: borderColor || DEFAULT_BORDER_COLOR,
      borderStyle: borderStyle,
    };
  }, [hasBorders, borderStyle, borderColor]);

  const borderRadius: CSSProperties = useMemo(() => {
    if (!useIndividualBorderRadius) {
      return { borderRadius: `${borderTopLeftRadius}px` };
    }

    return {
      borderTopLeftRadius: `${borderTopLeftRadius}px`,
      borderTopRightRadius: `${borderTopRightRadius}px`,
      borderBottomLeftRadius: `${borderBottomLeftRadius}px`,
      borderBottomRightRadius: `${borderBottomRightRadius}px`,
    };
  }, [
    useIndividualBorderRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
  ]);

  const borderWidth: CSSProperties = useMemo(() => {
    if (!hasBorders) {
      return {};
    }

    if (!useIndividualBorderWidth) {
      return { borderWidth: `${borderWidthTop}px` };
    }

    return {
      borderTopWidth: `${borderWidthTop}px`,
      borderLeftWidth: `${borderWidthLeft}px`,
      borderBottomWidth: `${borderWidthBottom}px`,
      borderRightWidth: `${borderWidthRight}px`,
    };
  }, [hasBorders, useIndividualBorderWidth, borderWidthTop, borderWidthLeft, borderWidthBottom, borderWidthRight]);

  return (
    <div
      className={section}
      style={
        {
          ...borderStyles,
          ...borderRadius,
          ...borderWidth,
          '--post-section-background-color': attrs.backgroundColor,
          '--post-section-margin-top': `${attrs.marginTop}px`,
          '--post-section-margin-left': `${attrs.marginLeft}px`,
          '--post-section-margin-right': `${attrs.marginRight}px`,
          '--post-section-margin-bottom': `${attrs.marginBottom}px`,
          '--post-section-padding-top': `${attrs.paddingTop}px`,
          '--post-section-padding-left': `${attrs.paddingLeft}px`,
          '--post-section-padding-right': `${attrs.paddingRight}px`,
          '--post-section-padding-bottom': `${attrs.paddingBottom}px`,
          // A section's color is consumed by elements within the section
          // Example; Footnote, Poll etc.
          '--post-section-text-color': color,
        } as CSSProperties
      }
      {...anchorAttributes}
    >
      {children}
    </div>
  );
};
